import {
  Avatar,
  Box,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import AddBoxOutlinedIcon from "@mui/icons-material/AddBoxOutlined";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import boardApi from "../../api/boardApi";
import { setBoards } from "../../redux/features/boardSlice";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import FavouriteList from "./FavouriteList";
import CategoryIcon from "@mui/icons-material/Category";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import AccountBoxIcon from "@mui/icons-material/AccountBox";
import AutoStoriesIcon from "@mui/icons-material/AutoStories";
import { toast } from "react-toastify";
import { toastErrorOptions } from "../../utils/constVals";
import AnalyticsOutlinedIcon from "@mui/icons-material/AnalyticsOutlined";
import LocalDiningOutlinedIcon from "@mui/icons-material/LocalDiningOutlined";

const drawerWidth = 312;

const Sidebar = ({ variant = "permanent", open = true, onClose }) => {
  const user = useSelector((state) => state.user.value);
  const boards = useSelector((state) => state.board.value);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { boardId } = useParams();
  const [activeIndex, setActiveIndex] = useState(0);
  const isTemporary = variant === "temporary";

  useEffect(() => {
    const getBoards = async () => {
      try {
        const res = await boardApi.getAll();
        dispatch(setBoards(res));
      } catch (err) {
        toast.error(err, toastErrorOptions);
      }
    };
    getBoards();
  }, [dispatch]);

  useEffect(() => {
    const activeItem = boards.findIndex((e) => e.id === boardId);
    setActiveIndex(activeItem);
  }, [boards, boardId]);

  const boardEntryPath = useMemo(
    () => (boards.length > 0 ? `/app/boards/${boards[0].id}` : "/app/boards"),
    [boards]
  );

  const closeIfTemporary = () => {
    if (isTemporary && onClose) {
      onClose();
    }
  };

  const navItems = [
    {
      label: "Dashboard",
      icon: <AnalyticsOutlinedIcon fontSize="small" />,
      href: "/app/dashboard",
    },
    {
      label: "Boards",
      icon: <CategoryIcon fontSize="small" />,
      href: boardEntryPath,
      disabled: boards.length === 0,
    },
    {
      label: "Foods",
      icon: <LocalDiningOutlinedIcon fontSize="small" />,
      href: "/foods",
    },
    {
      label: "Costs",
      icon: <AttachMoneyIcon fontSize="small" />,
      href: "/app/costs",
    },
    {
      label: "Profile",
      icon: <AccountBoxIcon fontSize="small" />,
      href: "/app/profile",
    },
    {
      label: "Tutorials",
      icon: <AutoStoriesIcon fontSize="small" />,
      href: "/app/dashboard",
    },
  ];

  const logout = () => {
    localStorage.removeItem("token");
    navigate("/");
    toast.error("Logged Out", toastErrorOptions);
    closeIfTemporary();
  };

  const onDragEnd = async ({ source, destination }) => {
    if (!destination) return;
    const newList = [...boards];
    const [removed] = newList.splice(source.index, 1);
    newList.splice(destination.index, 0, removed);
    const activeItem = newList.findIndex((e) => e.id === boardId);
    setActiveIndex(activeItem);
    dispatch(setBoards(newList));
    try {
      await boardApi.updatePosition({ boards: newList });
      toast.success("Category Position Updated", toastErrorOptions);
    } catch (err) {
      toast.error(err, toastErrorOptions);
    }
  };

  const addBoard = async () => {
    try {
      const res = await boardApi.create();
      const newList = [res, ...boards];
      dispatch(setBoards(newList));
      toast.success("Main Category Created", toastErrorOptions);
      navigate(`/app/boards/${res.id}`);
      closeIfTemporary();
    } catch (err) {
      toast.error(err, toastErrorOptions);
    }
  };

  const drawerPaperStyles = {
    width: drawerWidth,
    border: "none",
    background: "linear-gradient(180deg,#ffffff 0%,#f8fafc 60%,#eef2ff 100%)",
    display: "flex",
    flexDirection: "column",
    boxShadow: "0 20px 60px rgba(15,23,42,0.12)",
  };

  return (
    <Drawer
      variant={variant}
      open={variant === "permanent" ? true : open}
      onClose={onClose}
      ModalProps={{ keepMounted: true }}
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        "& .MuiDrawer-paper": drawerPaperStyles,
      }}
    >
      <Box
        sx={{
          px: 2.5,
          pt: 3,
          pb: 2,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Avatar
            sx={{
              width: 48,
              height: 48,
              fontSize: "1rem",
              fontWeight: 600,
              bgcolor: "primary.main",
            }}
          >
            {(user?.name || user?.username || "U").charAt(0).toUpperCase()}
          </Avatar>
          <Box>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              Workspace
            </Typography>
            <Typography variant="subtitle1" fontWeight={700}>
              {user?.name || user?.username}
            </Typography>
          </Box>
        </Stack>
      </Box>

      <Box sx={{ px: 2.5 }}>
        <List
          sx={{
            "& .MuiListItemButton-root": {
              borderRadius: 2,
              mb: 0.5,
              px: 1.5,
            },
            "& .MuiListItemButton-root:hover": {
              backgroundColor: "rgba(15,23,42,0.08)",
            },
          }}
        >
          {navItems.map((item) => (
            <ListItem disablePadding key={item.label}>
              <ListItemButton
                component={Link}
                to={item.href}
                disabled={item.disabled}
                onClick={closeIfTemporary}
              >
                <ListItemIcon
                  sx={{
                    color: "text.secondary",
                    minWidth: 40,
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography variant="body2" fontWeight={600}>
                      {item.label}
                    </Typography>
                  }
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Box>

      <Divider sx={{ my: 2, opacity: 0.6 }} />

      <Box
        sx={{
          flexGrow: 1,
          overflowY: "auto",
          px: 2.5,
          pb: 2,
          pr: 1,
          "&::-webkit-scrollbar": { width: 6 },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "rgba(15,23,42,0.2)",
            borderRadius: 999,
          },
        }}
      >
        <List
          disablePadding
          sx={{
            "& .MuiListItemButton-root": {
              borderRadius: 2,
            },
          }}
        >
          <ListItem sx={{ mb: 1 }}>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Welcome back
              </Typography>
              <Typography variant="subtitle1" fontWeight={600}>
                {user?.name || "Creator"}
              </Typography>
            </Box>
          </ListItem>
          <FavouriteList onNavigate={closeIfTemporary} />
          <Box sx={{ pt: 1.5 }} />

          <ListItem
            secondaryAction={
              <IconButton
                size="small"
                color="primary"
                onClick={addBoard}
                aria-label="Add category"
              >
                <AddBoxOutlinedIcon fontSize="small" />
              </IconButton>
            }
          >
            <Typography variant="body2" fontWeight={700}>
              Categories
            </Typography>
          </ListItem>

          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable
              key={"list-board-droppable"}
              droppableId={"list-board-droppable"}
            >
              {(provided) => (
                <div ref={provided.innerRef} {...provided.droppableProps}>
                  {boards.map((item, index) => (
                    <Draggable
                      key={item.id}
                      draggableId={item.id}
                      index={index}
                    >
                      {(provided, snapshot) => (
                        <ListItemButton
                          ref={provided.innerRef}
                          {...provided.dragHandleProps}
                          {...provided.draggableProps}
                          selected={index === activeIndex}
                          component={Link}
                          to={`./boards/${item.id}`}
                          onClick={closeIfTemporary}
                          sx={{
                            mb: 0.5,
                            px: 2,
                            cursor: snapshot.isDragging ? "grabbing" : "pointer",
                            "&.Mui-selected": {
                              backgroundColor: "rgba(99,102,241,0.12)",
                              color: "primary.main",
                              fontWeight: 700,
                            },
                          }}
                        >
                          <Typography
                            variant="body2"
                            fontWeight="600"
                            sx={{
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {item.icon} {item.title}
                          </Typography>
                        </ListItemButton>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </List>
      </Box>

      <Box sx={{ px: 2.5, pb: 3 }}>
        <ListItemButton
          onClick={logout}
          sx={{
            borderRadius: 2,
            backgroundColor: "rgba(248,113,113,0.12)",
            "&:hover": {
              backgroundColor: "rgba(248,113,113,0.2)",
            },
          }}
        >
          <ListItemIcon
            sx={{
              minWidth: 40,
              color: "error.main",
            }}
          >
            <LogoutOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary={
              <Typography variant="body2" fontWeight={700}>
                Logout
              </Typography>
            }
          />
        </ListItemButton>
      </Box>
    </Drawer>
  );
};

export default Sidebar;
